import { useMutation, type DataProxy } from '@apollo/client';
import { useHubble } from '@seek/hubble';
import type { Language } from '@seek/melways-sites';
import { metrics } from '@seek/metrics-js';
import { parse as parseCookies } from 'cookie';
import cloneDeep from 'lodash/cloneDeep';
import { useContext } from 'react';
import { v4 as uuid } from 'uuid';

import AnalyticsContext from 'src/analytics/AnalyticsContext';
import { LinkPosition } from 'src/analytics/adapter';
import type {
  JobDetailsDataLayer,
  RecommendedJobsAnalytics,
} from 'src/analytics/adapter/recommendedJobs';
import { useAppConfig } from 'src/components/App/useAppConfig';
import {
  CreateSavedJobDocument,
  DeleteSavedJobDocument,
  GetRecommendationsDocument,
} from 'src/graphql/graphql';
import { selectSmarterSearchCluster } from 'src/modules/featureFlag';
import logger from 'src/modules/logger';
import type {
  CreateSavedJob,
  CreateSavedJobSuccess,
  DeleteSavedJob,
  DeleteSavedJobSuccess,
} from 'src/mutations/types';
import type { RecommendedGlobalJob } from 'src/queries/types';

import { useLocale } from './useLocale';
import { useTimezone } from './useTimezone';

export type SaveJobOptions = Pick<
  RecommendedGlobalJob,
  'isSaved' | 'solMetadata'
> &
  Pick<RecommendedGlobalJob['job'], 'id'> & {
    linkPosition?: JobDetailsDataLayer['linkPosition'];
  };
interface UpdateCachePropsType {
  language: Language;
  locale: string;
  visitorId: string;
  zone: string;
  timezone: string;
}

type SavedJob = CreateSavedJob | DeleteSavedJob;

type SuccessfulSaveUnsaveMutation =
  | CreateSavedJobSuccess
  | DeleteSavedJobSuccess;

const isCreateSavedJob = (data: SavedJob): data is CreateSavedJob =>
  data.__typename === 'CreateSavedJobSuccess';

const isSuccessfulMutation = (
  data: SavedJob,
): data is SuccessfulSaveUnsaveMutation =>
  data.__typename === 'CreateSavedJobSuccess' ||
  data.__typename === 'DeleteSavedJobSuccess';

const getGlobalJobsWithIsSaved = (
  jobs: RecommendedGlobalJob[],
  savedJobId: string,
  hasCreated: boolean,
): RecommendedGlobalJob[] =>
  jobs.map((jobData: RecommendedGlobalJob) => {
    if (jobData.job.id === savedJobId) {
      return { ...jobData, isSaved: hasCreated };
    }
    return jobData;
  });

const updateCache =
  (updateCacheProps: UpdateCachePropsType) =>
  (cache: DataProxy, data: SavedJob) => {
    const {
      language: languageCode,
      locale,
      visitorId,
      zone,
      timezone,
    } = updateCacheProps;

    if (!isSuccessfulMutation(data)) {
      return;
    }

    const savedJobId = data.id;
    const cluster = selectSmarterSearchCluster();

    const cacheData = cache.readQuery({
      query: GetRecommendationsDocument,
      variables: {
        cluster,
        languageCode,
        locale,
        visitorId,
        zone,
        timezone,
      },
    });

    const newCacheData = cloneDeep(cacheData);
    const recommendedGlobalJobs =
      newCacheData?.viewer?.recommendations?.recommendedGlobalJobs;
    const wasCreateOperation = isCreateSavedJob(data);

    if (recommendedGlobalJobs) {
      recommendedGlobalJobs.globalJobs = getGlobalJobsWithIsSaved(
        recommendedGlobalJobs.globalJobs,
        savedJobId,
        wasCreateOperation,
      );

      cache.writeQuery({
        query: GetRecommendationsDocument,
        data: newCacheData!,
        variables: {
          cluster,
          languageCode,
          locale,
          visitorId,
          zone,
          timezone,
        },
      });
    }
  };

export default function useSaveJob({
  id,
  isSaved,
  linkPosition = LinkPosition.Listing,
  solMetadata,
}: SaveJobOptions) {
  const { language, zone } = useAppConfig();
  const locale = useLocale();
  const hubble = useHubble();
  const visitorId = hubble.visitorId();
  const timezone = useTimezone();

  const [createSavedJob] = useMutation(CreateSavedJobDocument, {
    update(cache, { data, errors }, { variables }) {
      if (errors) {
        errors?.forEach((error) => {
          logger('Failed to create saved job', { error });
        });
        return;
      }

      if (!data) {
        logger('Failed to create saved job', {
          error: new Error(`Could not save job: ${variables?.input.id}`),
        });
        return;
      }

      updateCache({
        language,
        locale,
        visitorId,
        zone,
        timezone,
      })(cache, data.createSavedJob2);
    },
  });

  const [deleteSavedJob] = useMutation(DeleteSavedJobDocument, {
    update(cache, { data, errors }, { variables }) {
      if (errors) {
        errors?.forEach((error) => {
          logger('Failed to delete saved job', { error });
        });
        return;
      }

      if (!data) {
        logger('Failed to delete saved job', {
          error: new Error(
            `Could not delete saved job: ${variables?.input.id}`,
          ),
        });
        return;
      }

      updateCache({
        language,
        locale,
        visitorId,
        zone,
        timezone,
      })(cache, data.deleteSavedJob2);
    },
  });

  const { jobSaved, jobUnsaved } = useContext(
    AnalyticsContext,
  ) as RecommendedJobsAnalytics;

  const toggleSavedJob = async (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    const { JobseekerSessionId } = parseCookies(window.document.cookie);
    const trackingCorrelationId = uuid();
    if (Boolean(isSaved)) {
      await deleteSavedJob({
        variables: {
          input: {
            id,
            eventCaptureData: {
              channel: 'web',
              view: 'recommendationsList',
              eventCaptureSessionId: JobseekerSessionId ?? '',
            },
          },
          locale,
        },
      });
      metrics.count('saveJob', [`isSaved:${isSaved}`]);
      jobUnsaved({ jobId: id, linkPosition, trackingCorrelationId });
      hubble.trackEvent(
        'job_unsave_pressed',
        { jobId: id, solMetadata },
        { trackingCorrelationId },
      );
    } else {
      await createSavedJob({
        variables: {
          input: {
            id,
            zone,
            eventCaptureData: {
              channel: 'web',
              view: 'recommendationsList',
              eventCaptureSessionId: JobseekerSessionId ?? '',
            },
          },
          locale,
        },
      });
      metrics.count('saveJob', [`isSaved:${isSaved}`]);
      jobSaved({ jobId: id, linkPosition, trackingCorrelationId });
      hubble.trackEvent(
        'job_save_pressed',
        { jobId: id, solMetadata },
        { trackingCorrelationId },
      );
    }
  };

  return {
    toggleSavedJob,
  };
}
